import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { TeamPhoto } from '../../components/TeamPhoto';
import CarrierAffiliations from '../../components/CarrierAffiliations';
import {
  Save, Plus, Trash2, Edit, X, Eye, EyeOff, Loader2, Image as ImageIcon,
  Star, Mail, User, Phone, Lock, Shield, FileText, ChevronDown, ChevronUp
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AgentModal = ({ agent, onSave, onClose, saving }) => {
  const [formData, setFormData] = useState({
    name: agent?.name || '',
    slug: agent?.slug || '',
    email: agent?.email || '',
    phone: agent?.phone || '',
    photo: agent?.photo || '',
    role: agent?.role || 'Seg Funds & Insurance Advisor',
    provinces: agent?.provinces || 'MB licensed',
    licenses: agent?.licenses || [],
    license_details: agent?.license_details || '',
    commission_portal_url: agent?.commission_portal_url || '',
    birthday: agent?.birthday || '',
    active: agent?.active ?? true
  });
  // Hide the "external URL" input behind a small toggle so the relative
  // /api/uploads/... path from a fresh upload doesn't trip the URL-validator
  // tooltip ("Please enter a URL").
  const [showUrlInput, setShowUrlInput] = useState(false);

  const generateSlug = (name) => {
    return name.toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  };

  const handleNameChange = (e) => {
    const name = e.target.value;
    setFormData({
      ...formData,
      name,
      slug: agent?.slug || generateSlug(name)
    });
  };

  const toggleLicense = (license) => {
    const current = formData.licenses || [];
    if (current.includes(license)) {
      setFormData({ ...formData, licenses: current.filter(l => l !== license) });
    } else {
      setFormData({ ...formData, licenses: [...current, license] });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-start justify-center z-50 p-4 overflow-y-auto">
      <Card className="w-full max-w-lg my-8 max-h-[85vh] flex flex-col">
        <CardHeader className="flex flex-row items-center justify-between border-b flex-shrink-0">
          <CardTitle>{agent ? 'Edit Agent' : 'New Insurance Agent'}</CardTitle>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="h-5 w-5" />
          </Button>
        </CardHeader>
        <CardContent className="p-6 overflow-y-auto flex-1">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Agent Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={handleNameChange}
                required
                placeholder="John Smith"
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0077B6]"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1">URL Slug *</label>
              <div className="flex items-center">
                <span className="text-gray-500 text-sm mr-2">/agent/</span>
                <input
                  type="text"
                  value={formData.slug}
                  onChange={(e) => setFormData({ ...formData, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                  required
                  placeholder="john-smith"
                  className="flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0077B6]"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">This will be the agent's unique URL</p>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Email *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  placeholder="john@utmostfinancial.ca"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0077B6]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Phone *</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  required
                  placeholder="204-555-1234"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0077B6]"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-2">Agent Photo *</label>
              
              {/* File Upload Option */}
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-[#0077B6] transition-colors">
                <input
                  type="file"
                  id="photo-upload"
                  accept="image/*"
                  className="hidden"
                  onChange={async (e) => {
                    const file = e.target.files[0];
                    if (!file) return;
                    
                    // Show loading state
                    setFormData({ ...formData, photo: 'uploading...' });
                    
                    const uploadFormData = new FormData();
                    uploadFormData.append('file', file);
                    
                    try {
                      const response = await fetch(`${API_URL}/api/upload/photo`, {
                        method: 'POST',
                        body: uploadFormData
                      });
                      
                      if (response.ok) {
                        const data = await response.json();
                        // Save only the RELATIVE path so the photo URL never gets
                        // locked to a specific preview/deploy subdomain. The TeamPhoto
                        // component prepends REACT_APP_BACKEND_URL at render time.
                        setFormData({ ...formData, photo: data.url });
                      } else {
                        alert('Failed to upload photo');
                        setFormData({ ...formData, photo: '' });
                      }
                    } catch (err) {
                      console.error('Upload error:', err);
                      alert('Error uploading photo');
                      setFormData({ ...formData, photo: '' });
                    }
                  }}
                />
                <label htmlFor="photo-upload" className="cursor-pointer">
                  <div className="text-gray-500">
                    <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                      <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    <p className="mt-1 text-sm">Click to upload a photo</p>
                    <p className="text-xs text-gray-400">PNG, JPG, GIF up to 10MB</p>
                  </div>
                </label>
              </div>
              
              {/* OR divider — collapsed by default to avoid the form-validation
                  confusion when the field shows a relative /api/uploads/... path. */}
              {!showUrlInput ? (
                <button
                  type="button"
                  onClick={() => setShowUrlInput(true)}
                  className="block mx-auto mt-2 text-xs text-[#0077B6] hover:underline"
                  data-testid="show-url-input-btn"
                >
                  …or paste an external image URL instead
                </button>
              ) : (
                <>
                  <div className="flex items-center my-3">
                    <div className="flex-1 border-t border-gray-300"></div>
                    <span className="px-3 text-gray-500 text-sm">External image URL</span>
                    <div className="flex-1 border-t border-gray-300"></div>
                  </div>
                  {/* type="text" not "url" so relative `/api/uploads/...` paths from
                      previous uploads are also valid (they pass form submit cleanly). */}
                  <input
                    type="text"
                    value={formData.photo === 'uploading...' ? '' : formData.photo}
                    onChange={(e) => setFormData({ ...formData, photo: e.target.value })}
                    placeholder="https://example.com/photo.jpg  or  /api/uploads/...png"
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0077B6]"
                    data-testid="photo-url-input"
                  />
                </>
              )}
              
              {/* Preview */}
              {formData.photo && formData.photo !== 'uploading...' && (
                <div className="mt-3 flex justify-center">
                  <div className="w-24 h-24 rounded-full overflow-hidden border-2 border-[#D4AF37]">
                    <TeamPhoto src={formData.photo} name={formData.name} className="w-full h-full object-cover" />
                  </div>
                </div>
              )}
              {formData.photo === 'uploading...' && (
                <div className="mt-3 flex justify-center">
                  <div className="w-24 h-24 rounded-full bg-gray-100 flex items-center justify-center">
                    <Loader2 className="h-8 w-8 animate-spin text-[#0077B6]" />
                  </div>
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Role/Title *</label>
                <input
                  type="text"
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  required
                  placeholder="Seg Funds & Insurance Advisor"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0077B6]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Licensed Provinces *</label>
                <input
                  type="text"
                  value={formData.provinces}
                  onChange={(e) => setFormData({ ...formData, provinces: e.target.value })}
                  required
                  placeholder="MB licensed"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0077B6]"
                />
              </div>
            </div>

            {/* License Types for Compliance */}
            <div>
              <label className="block text-sm font-medium mb-2">License Types (for compliance display)</label>
              <div className="flex flex-wrap gap-3">
                <label className="flex items-center gap-2 bg-gray-100 px-3 py-2 rounded-lg cursor-pointer hover:bg-gray-200">
                  <input
                    type="checkbox"
                    checked={formData.licenses?.includes('mortgage')}
                    onChange={() => toggleLicense('mortgage')}
                    className="w-4 h-4 text-[#003D5C] rounded"
                  />
                  <span className="text-sm font-medium">Mortgage</span>
                </label>
                <label className="flex items-center gap-2 bg-gray-100 px-3 py-2 rounded-lg cursor-pointer hover:bg-gray-200">
                  <input
                    type="checkbox"
                    checked={formData.licenses?.includes('insurance')}
                    onChange={() => toggleLicense('insurance')}
                    className="w-4 h-4 text-[#0077B6] rounded"
                  />
                  <span className="text-sm font-medium">Insurance</span>
                </label>
                <label className="flex items-center gap-2 bg-gray-100 px-3 py-2 rounded-lg cursor-pointer hover:bg-gray-200">
                  <input
                    type="checkbox"
                    checked={formData.licenses?.includes('investments')}
                    onChange={() => toggleLicense('investments')}
                    className="w-4 h-4 text-[#D4AF37] rounded"
                  />
                  <span className="text-sm font-medium">Investments/Seg Funds</span>
                </label>
              </div>
            </div>

            {/* License Details for Compliance */}
            <div>
              <label className="block text-sm font-medium mb-1">License Details (optional compliance text)</label>
              <textarea
                value={formData.license_details}
                onChange={(e) => setFormData({ ...formData, license_details: e.target.value })}
                placeholder="e.g., Licensed mortgage broker under DLC Utmost Financial. Life & Health Insurance License #12345."
                rows={2}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0077B6]"
              />
            </div>

            {/* Birthday (Calendar Autopilot) */}
            <div>
              <label className="block text-sm font-medium mb-1">Date of Birth</label>
              <input
                type="date"
                value={formData.birthday || ''}
                onChange={(e) => setFormData({ ...formData, birthday: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0077B6]"
                data-testid="agent-birthday-input"
              />
              <p className="text-xs text-gray-500 mt-1">Used by the Calendar Autopilot for the team calendar and the birthday greeting email</p>
            </div>

            {/* Commission Portal URL */}
            <div>
              <label className="block text-sm font-medium mb-1">Commission Portal URL</label>
              <input
                type="url"
                value={formData.commission_portal_url}
                onChange={(e) => setFormData({ ...formData, commission_portal_url: e.target.value })}
                placeholder="https://portal.example.com/agent/123"
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0077B6]"
              />
              <p className="text-xs text-gray-500 mt-1">Link to agent's commission portal (visible in their Agent Portal)</p>
            </div>
            
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="active"
                checked={formData.active}
                onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                className="w-4 h-4 text-[#0077B6] rounded"
              />
              <label htmlFor="active" className="text-sm font-medium">Active (agent website is visible)</label>
            </div>

            <div className="bg-blue-50 p-3 rounded-lg text-sm text-blue-800">
              <strong>Note:</strong> When you create an agent, login credentials will be auto-generated and emailed to them. They can access their portal at <code>/agent-login</code>
            </div>

            {agent?.id && (
              <div className="border rounded-lg p-4 bg-gray-50">
                <h3 className="font-semibold text-[#003D5C] mb-2 flex items-center gap-2">
                  Carrier Affiliations
                </h3>
                <CarrierAffiliations agentId={agent.id} embedded />
              </div>
            )}
            
            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" onClick={onClose} className="flex-1">
                Cancel
              </Button>
              <Button type="submit" disabled={saving} className="flex-1 bg-[#0077B6]">
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    {agent ? 'Update Agent' : 'Create Agent'}
                  </>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

// Referrer Modal Component
